import { supabase } from '@/integrations/supabase/client';

// Real tenant data snapshot sent to AI — every field here must trace to a
// real synced row (projects_sync/tasks_sync/meetings_sync/emails_sync/
// risk_signals). Fields with no real data source in this schema (AI time
// saved, productivity %, stakeholder update counts, team chat volume) are
// simply omitted rather than filled with plausible-looking placeholders —
// this replaced a hardcoded "Digital Transformation Initiative" demo
// project that was being sent to the LLM regardless of tenant.
const DONE_STATUSES = ['done', 'closed', 'resolved', 'complete', 'completed'];
const BLOCKED_STATUSES = ['blocked', 'block'];

export const buildProjectDataSnapshot = async (days = 7) => {
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  const [projectsRes, tasksRes, meetingsRes, emailsRes, signalsRes] = await Promise.all([
    supabase.from('projects_sync').select('name, budget_total, budget, budget_spent, spent, milestones_data'),
    supabase.from('tasks_sync').select('status, assignee'),
    supabase.from('meetings_sync').select('duration_minutes, ai_summary, ai_action_items').gte('start_time', since),
    supabase.from('emails_sync').select('id').gte('created_at', since),
    supabase.from('risk_signals').select('severity, rule_id').is('resolved_at', null),
  ]);

  const projects = projectsRes.data ?? [];
  const tasks = tasksRes.data ?? [];
  const meetings = meetingsRes.data ?? [];
  const emails = emailsRes.data ?? [];
  const signals = signalsRes.data ?? [];

  const allocated = projects.reduce((sum, p) => sum + Number(p.budget_total ?? p.budget ?? 0), 0);
  const spent = projects.reduce((sum, p) => sum + Number(p.budget_spent ?? p.spent ?? 0), 0);

  const completed = tasks.filter((t) => DONE_STATUSES.includes((t.status ?? '').toLowerCase())).length;
  const blocked = tasks.filter((t) => BLOCKED_STATUSES.some((b) => (t.status ?? '').toLowerCase().includes(b))).length;
  const inProgress = tasks.length - completed - blocked;

  const teamMembers = Array.from(new Set(tasks.map((t) => t.assignee).filter((a): a is string => Boolean(a))));

  const actionItemsGenerated = meetings.reduce(
    (sum, m) => sum + (Array.isArray(m.ai_action_items) ? m.ai_action_items.length : 0),
    0,
  );
  const totalDuration = meetings.reduce((sum, m) => sum + Number(m.duration_minutes ?? 0), 0);

  return {
    projectName: projects.length === 1 ? projects[0].name : `Portfolio (${projects.length} projects)`,
    period: `${new Date(since).toLocaleDateString()} - ${new Date().toLocaleDateString()}`,
    team: {
      size: teamMembers.length,
      members: teamMembers.slice(0, 8),
    },
    tasks: {
      completed,
      inProgress: Math.max(inProgress, 0),
      blocked,
      total: tasks.length,
    },
    budget: allocated > 0 ? {
      allocated,
      spent,
      remaining: allocated - spent,
      percentUsed: Math.round((spent / allocated) * 1000) / 10,
    } : undefined,
    risks: signals.map((s) => ({ name: s.rule_id.replace(/_/g, ' '), severity: s.severity, status: 'open' })),
    meetings: {
      held: meetings.length,
      actionItemsGenerated,
      avgDuration: meetings.length > 0 ? `${Math.round(totalDuration / meetings.length)} mins` : 'N/A',
    },
    communications: {
      emailsProcessed: emails.length,
    },
  };
};
